/**
 * SignatureForm - Add/Edit signature on a collection item
 * MDM-UNIFIED-001 Phase 7: Signature Tracking
 */
import React, { useState, useEffect } from 'react';
import {
  X, PenTool, User, MapPin, Calendar, FileText,
  Shield, Camera, DollarSign, Save, Loader2, Search
} from 'lucide-react';
import { createSignature, updateSignature, getSignaturePremiumEstimate } from '../../services/vaultApi';
import { fetchAPI } from '../../services/api';
import SignaturePremiumBadge, { SignaturePremiumTierBadge } from './SignaturePremiumBadge';

// Witness type options
const WITNESS_TYPES = [
  { value: '', label: 'Not Witnessed' },
  { value: 'cgc_ss', label: 'CGC Signature Series' },
  { value: 'cbcs_verified', label: 'CBCS Verified Signature' },
  { value: 'self', label: 'Self-Witnessed' },
];

// Signature location options
const LOCATION_OPTIONS = [
  { value: '', label: 'Not specified' },
  { value: 'cover', label: 'Cover' },
  { value: 'interior', label: 'Interior Page' },
  { value: 'cgc_label', label: 'CGC Label' },
  { value: 'cbcs_label', label: 'CBCS Label' },
  { value: 'back_cover', label: 'Back Cover' },
];

export default function SignatureForm({ collectionItemId, signature, onClose, onSave }) {
  const isEditing = !!signature;

  const [formData, setFormData] = useState({
    collection_item_id: collectionItemId,
    signer_name: '',
    creator_id: null,
    signature_location: '',
    signature_date: '',
    signature_notes: '',
    obtained_at: '',
    obtained_date: '',
    is_witnessed: false,
    witness_type: '',
    is_verified: false,
    proof_photo_url: '',
    estimated_premium_pct: '',
    premium_value: '',
  });

  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorResults, setCreatorResults] = useState([]);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  // Initialize form with existing signature data
  useEffect(() => {
    if (signature) {
      setFormData({
        collection_item_id: signature.collection_item_id,
        signer_name: signature.signer_name || '',
        creator_id: signature.creator_id,
        signature_location: signature.signature_location || '',
        signature_date: signature.signature_date || '',
        signature_notes: signature.signature_notes || '',
        obtained_at: signature.obtained_at || '',
        obtained_date: signature.obtained_date || '',
        is_witnessed: signature.is_witnessed || false,
        witness_type: signature.witness_type || '',
        is_verified: signature.is_verified || false,
        proof_photo_url: signature.proof_photo_url || '',
        estimated_premium_pct: signature.estimated_premium_pct || '',
        premium_value: signature.premium_value || '',
      });
      if (signature.creator_id && signature.creator_name) {
        setSelectedCreator({ id: signature.creator_id, name: signature.creator_name });
      }
    }
  }, [signature]);

  // Search creators
  const searchCreators = async (query) => {
    if (query.length < 2) {
      setCreatorResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // FE-028: this called `/creators/search?q=&limit=` -- a path the API has never served,
      // so creator autocomplete 404'd on every keystroke and the catch below silently emptied
      // the list. The real endpoint is GET /comics/creators (backend fn: search_creators),
      // which takes `name` + `page` and returns the standard page envelope
      // { count, results: [{id, name, desc, image}], source, next, previous }.
      const page = await fetchAPI(`/comics/creators?name=${encodeURIComponent(query)}&page=1`);
      setCreatorResults(page.results || []);
    } catch (err) {
      console.error('Creator search failed:', err);
      setCreatorResults([]);
    }
    setIsSearching(false);
  };

  // Debounced creator search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (creatorSearch) {
        searchCreators(creatorSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [creatorSearch]);

  const handleCreatorSelect = (creator) => {
    setSelectedCreator(creator);
    setFormData({ ...formData, creator_id: creator.id, signer_name: creator.name });
    setCreatorSearch('');
    setCreatorResults([]);
  };

  const handleClearCreator = () => {
    setSelectedCreator(null);
    setFormData({ ...formData, creator_id: null });
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });

    // Auto-set is_witnessed when witness_type is selected
    if (field === 'witness_type' && value) {
      setFormData({ ...formData, [field]: value, is_witnessed: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.signer_name.trim()) {
      setError('Signer name is required');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        signature_date: formData.signature_date || null,
        obtained_date: formData.obtained_date || null,
        estimated_premium_pct: formData.estimated_premium_pct ? parseFloat(formData.estimated_premium_pct) : null,
        premium_value: formData.premium_value ? parseFloat(formData.premium_value) : null,
      };

      if (isEditing) {
        await updateSignature(signature.id, payload);
      } else {
        await createSignature(payload);
      }

      onSave?.();
    } catch (err) {
      console.error('Failed to save signature:', err);
      setError(err.message || 'Failed to save signature');
    }
    setIsSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-purple-400" />
            <h3 className="font-semibold text-white">
              {isEditing ? 'Edit Signature' : 'Add Signature'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-zinc-800 rounded transition-colors"
          >
            <X className="w-5 h-5 text-zinc-400" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Signer Name */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">
              <User className="w-4 h-4 inline mr-1" />
              Signer Name *
            </label>
            <input
              type="text"
              value={formData.signer_name}
              onChange={(e) => handleChange('signer_name', e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
              placeholder="e.g., Jim Lee"
              required
            />
          </div>

          {/* Creator Link */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">
              <Search className="w-4 h-4 inline mr-1" />
              Link to Creator (optional)
            </label>
            {selectedCreator ? (
              <div className="flex items-center gap-2 bg-zinc-800 border border-purple-500/50 rounded-lg px-3 py-2">
                <span className="flex-1 text-white">{selectedCreator.name}</span>
                <button
                  type="button"
                  onClick={handleClearCreator}
                  className="text-zinc-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={creatorSearch}
                  onChange={(e) => setCreatorSearch(e.target.value)}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
                  placeholder="Search creators..."
                />
                {/* c8 ignore next 4 */}
                {isSearching && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 text-zinc-400 animate-spin" />
                  </div>
                )}
                {creatorResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {creatorResults.map((creator) => (
                      <button
                        key={creator.id}
                        type="button"
                        onClick={() => handleCreatorSelect(creator)}
                        className="w-full px-3 py-2 text-left hover:bg-zinc-700 text-white text-sm"
                      >
                        {creator.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Signature Location */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">
              <MapPin className="w-4 h-4 inline mr-1" />
              Signature Location
            </label>
            <select
              value={formData.signature_location}
              onChange={(e) => handleChange('signature_location', e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
            >
              {LOCATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Obtained At / Date */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">
                Where Obtained
              </label>
              <input
                type="text"
                value={formData.obtained_at}
                onChange={(e) => handleChange('obtained_at', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
                placeholder="e.g., NYCC 2025"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1" />
                When
              </label>
              <input
                type="date"
                value={formData.obtained_date}
                onChange={(e) => handleChange('obtained_date', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Witness Type */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">
              <Shield className="w-4 h-4 inline mr-1" />
              Verification Status
            </label>
            <select
              value={formData.witness_type}
              onChange={(e) => handleChange('witness_type', e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:border-purple-500 focus:outline-none"
            >
              {WITNESS_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Premium Estimate Preview */}
          {formData.signer_name && (
            <div className="border-t border-zinc-700/50 pt-4">
              <label className="block text-sm text-zinc-400 mb-2">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Estimated Premium
              </label>
              <SignaturePremiumBadge
                signerName={formData.signer_name}
                creatorId={formData.creator_id}
                witnessType={formData.witness_type}
                showDetails={true}
              />
            </div>
          )}

          {/* Premium Estimate */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">
                <DollarSign className="w-4 h-4 inline mr-1" />
                Premium %
              </label>
              <input
                type="number"
                value={formData.estimated_premium_pct}
                onChange={(e) => handleChange('estimated_premium_pct', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
                placeholder="e.g., 25"
                step="0.01"
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm text-zinc-400 mb-1.5">
                Premium Value ($)
              </label>
              <input
                type="number"
                value={formData.premium_value}
                onChange={(e) => handleChange('premium_value', e.target.value)}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
                placeholder="e.g., 500"
                step="0.01"
                min="0"
              />
            </div>
          </div>

          {/* Proof Photo URL */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">
              <Camera className="w-4 h-4 inline mr-1" />
              Proof Photo URL
            </label>
            <input
              type="url"
              value={formData.proof_photo_url}
              onChange={(e) => handleChange('proof_photo_url', e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none"
              placeholder="https://..."
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-zinc-400 mb-1.5">
              <FileText className="w-4 h-4 inline mr-1" />
              Notes
            </label>
            <textarea
              value={formData.signature_notes}
              onChange={(e) => handleChange('signature_notes', e.target.value)}
              className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:border-purple-500 focus:outline-none resize-none"
              placeholder="Any additional notes about this signature..."
              rows={3}
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-zinc-800">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-zinc-700 text-white rounded-lg transition-colors flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditing ? 'Update' : 'Add'} Signature
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
